import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDown,
  ShieldCheck,
  Handshake,
  Scale,
  GraduationCap,
  Building2,
  Users,
  Briefcase,
  Tractor,
  HeartHandshake,
  School,
  Wrench,
  CircleDot,
  Sparkles,
  FileCheck2,
  Compass,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import Emblem from "@/components/Emblem";
import WhatsAppForm from "@/components/WhatsAppForm";
import { buildWhatsAppLink } from "@/lib/constants";

const Home = () => {
  return (
    <div className="bc-fade-up">
      <Hero />
      <MarqueeStrip />
      <Mission />
      <WhatWeDo />
      <WhoItIsFor />
      <HowItWorks />
      <TrustGovernance />
      <PhysicalDigital />
      <FoundersNote />
      <JoinSection />
    </div>
  );
};

/* -------------------------------- HERO -------------------------------- */

const Hero = () => {
  return (
    <section
      className="relative overflow-hidden pt-8 md:pt-14 pb-20 md:pb-32"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bc-grid bc-grid-fade opacity-60" />
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(31,122,90,0.18), transparent 70%)",
            filter: "blur(20px)",
          }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(214,178,94,0.12), transparent 70%)",
            filter: "blur(40px)",
          }}
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-4 md:gap-6 items-start">
          {/* Side index */}
          <div className="hidden lg:flex col-span-1 flex-col items-start pt-4">
            <div className="bc-vlabel">Hero · 01</div>
          </div>

          {/* Headline */}
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-block w-8 h-px bg-[#D6B25E]" />
                <span className="eyebrow">A Trusted Common</span>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h1
                className="font-serif-display text-[36px] sm:text-5xl md:text-6xl lg:text-[88px] leading-[1.05] tracking-tight"
                data-testid="hero-heading"
              >
                A trusted common
                <br />
                for{" "}
                <span className="italic text-[#D6B25E]">identity</span>,{" "}
                <span className="italic text-[#58D2C7]">work</span>,
                <br />
                service & community-
                <br />
                led growth.
              </h1>
            </Reveal>

            <Reveal delay={260}>
              <p className="mt-10 max-w-xl text-[#6F7787] text-base md:text-lg leading-relaxed">
                Believers Common is a mission-led platform where people,
                professionals, businesses, and institutions can collaborate
                through verified identity, accountable service, transparent
                governance, and shared opportunity.
              </p>
            </Reveal>

            <Reveal delay={380}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={buildWhatsAppLink(
                    "Hello Believers Common, I'd like to register my interest in joining the founding cohort."
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid="hero-cta-whatsapp"
                >
                  Become a founding member
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
                </a>
                <a
                  href="#what-we-do"
                  className="btn-ghost"
                  data-testid="hero-cta-explore"
                >
                  Explore the commons
                  <ArrowDown className="w-4 h-4" strokeWidth={1.6} />
                </a>
              </div>
            </Reveal>

            <Reveal delay={520}>
              <div className="mt-12 md:mt-16 grid grid-cols-3 gap-4 md:gap-6 max-w-xl">
                <Stat n="01" label="Founding mission" />
                <Stat n="05" label="Participation groups" />
                <Stat n="∞" label="Commons in motion" />
              </div>
            </Reveal>
          </div>

          {/* Emblem */}
          <div className="col-span-12 lg:col-span-4 flex items-center justify-center lg:justify-end mt-8 lg:mt-0">
            <Reveal delay={300} className="relative">
              <Emblem size={520} />
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-center w-full">
                <div className="eyebrow-slate">The Common</div>
                <div className="font-serif-display italic text-[#D6B25E] text-2xl mt-1">
                  est. mission
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ n, label }) => (
  <div>
    <div className="font-serif-display text-4xl md:text-5xl text-[#F6F7FB]">{n}</div>
    <div className="mt-2 text-[10px] md:text-[11px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-[#6F7787] leading-snug">
      {label}
    </div>
  </div>
);

/* ------------------------------- MARQUEE ------------------------------- */

const MarqueeStrip = () => {
  const items = [
    "Trust before transaction",
    "Service with dignity",
    "Governance before growth",
    "No hidden privilege",
    "Evidence-based action",
    "Community-led prosperity",
    "Balance & responsibility",
    "Learning & opportunity",
  ];
  const row = (
    <div className="bc-marquee" data-testid="marquee-strip">
      {items.concat(items).map((t, i) => (
        <span
          key={i}
          className="font-serif-display italic text-[#F6F7FB]/80 text-2xl md:text-4xl whitespace-nowrap"
        >
          <span className="text-[#D6B25E] mr-4">◆</span>
          {t}
        </span>
      ))}
    </div>
  );
  return (
    <section
      className="relative border-y border-white/5 py-6 md:py-8 overflow-hidden bg-[#0A0D14]"
      aria-label="Believers Common values"
    >
      {row}
    </section>
  );
};

/* ------------------------------- MISSION ------------------------------- */

const Mission = () => {
  return (
    <section
      id="mission"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10"
      data-testid="mission-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <Reveal>
            <div className="eyebrow mb-4">02 · Mission</div>
            <div className="font-serif-display text-3xl text-[#D6B25E] italic">
              Why we exist.
            </div>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-9">
          <Reveal delay={100}>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.12] max-w-4xl">
              Believers Common exists to create a{" "}
              <span className="text-[#D6B25E]">trusted community platform</span>{" "}
              where people, professionals, service providers, institutions, and
              enterprises can collaborate through{" "}
              <span className="italic text-[#58D2C7]">verified identity</span>,{" "}
              <span className="italic text-[#58D2C7]">accountable service</span>,{" "}
              <span className="italic text-[#58D2C7]">transparent governance</span>
              , and shared economic opportunity.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            <Pillar
              icon={<ShieldCheck className="w-5 h-5" strokeWidth={1.4} />}
              title="Trust"
              text="Identity, proof and accountability before any transaction."
            />
            <Pillar
              icon={<Handshake className="w-5 h-5" strokeWidth={1.4} />}
              title="Service"
              text="Dignity for every contributor — workers, agents, and operators."
            />
            <Pillar
              icon={<Scale className="w-5 h-5" strokeWidth={1.4} />}
              title="Governance"
              text="Clear rules, fair pricing, and protection from hidden privilege."
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Pillar = ({ icon, title, text }) => (
  <Reveal className="bg-[#0A0D14] p-8 hover:bg-[#0F1320] transition-colors">
    <div className="flex items-center gap-3 text-[#58D2C7] mb-6">
      {icon}
      <span className="text-[11px] tracking-[0.28em] uppercase">Pillar</span>
    </div>
    <h3 className="font-serif-display text-3xl mb-3">{title}</h3>
    <p className="text-[#6F7787] leading-relaxed">{text}</p>
  </Reveal>
);

/* ------------------------------- WHAT WE DO ------------------------------- */

const ACTIVITIES = [
  {
    no: "01",
    title: "Verified Member Network",
    text: "A trusted registry of members, professionals, businesses, riders, agents, validators, and community participants.",
    icon: <Users className="w-5 h-5" strokeWidth={1.4} />,
    accent: "#D6B25E",
  },
  {
    no: "02",
    title: "Service & Work Marketplace",
    text: "Offer and access services through transparent rules, fair pricing bands, milestone work, and dispute-safe engagement.",
    icon: <Briefcase className="w-5 h-5" strokeWidth={1.4} />,
    accent: "#58D2C7",
  },
  {
    no: "03",
    title: "Governance & Mediation",
    text: "Operating rules, member responsibilities, escalation paths and mediation for fair, accountable participation.",
    icon: <Scale className="w-5 h-5" strokeWidth={1.4} />,
    accent: "#1F7A5A",
  },
  {
    no: "04",
    title: "Youth, Internships & Skill Pathways",
    text: "Connect apprentices, interns and learners to real work, workshops, factories, mentors and practical projects.",
    icon: <GraduationCap className="w-5 h-5" strokeWidth={1.4} />,
    accent: "#D6B25E",
  },
  {
    no: "05",
    title: "Physical & Digital Commons",
    text: "Hubs, workshops, local nodes and digital systems woven into one trusted operating environment.",
    icon: <Building2 className="w-5 h-5" strokeWidth={1.4} />,
    accent: "#58D2C7",
  },
];

const WhatWeDo = () => {
  return (
    <section
      id="what-we-do"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      data-testid="what-we-do-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <div className="eyebrow mb-4">03 · What We Do</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Five tracks. <span className="italic text-[#D6B25E]">One commons.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5">
            <Reveal delay={140}>
              <p className="text-[#6F7787] text-base md:text-lg leading-relaxed">
                Believers Common is not a marketplace alone. It is a governed
                trust infrastructure connecting members, services, evidence,
                pricing, and dispute handling.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {ACTIVITIES.map((a, i) => (
            <Reveal key={a.no} delay={i * 80}>
              <div
                className="bc-card group p-10 h-full bg-[#0A0D14] relative"
                data-testid={`activity-card-${a.no}`}
              >
                <div className="flex items-start justify-between mb-10">
                  <div
                    className="w-12 h-12 border flex items-center justify-center"
                    style={{ borderColor: a.accent + "55", color: a.accent }}
                  >
                    {a.icon}
                  </div>
                  <div className="font-serif-display text-3xl text-[#6F7787]/60 group-hover:text-[#D6B25E] transition-colors">
                    {a.no}
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mb-4">
                  {a.title}
                </h3>
                <p className="text-[#6F7787] leading-relaxed">{a.text}</p>
                <div className="mt-10 flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#6F7787] group-hover:text-[#D6B25E] transition-colors">
                  Track <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={400}>
            <div className="p-10 h-full bg-[#0A0D14] flex flex-col justify-between">
              <div className="font-serif-display text-2xl italic text-[#D6B25E]">
                More tracks under
                <br /> development.
              </div>
              <div className="text-[#6F7787] text-sm leading-relaxed mt-6">
                Civic, factory, education, marketplace and governance pilots are
                being scoped under the founding mission.
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- WHO IT IS FOR ------------------------------- */

const AUDIENCE = [
  { icon: <GraduationCap className="w-5 h-5" />, label: "Youth & Learners", note: "Internships · apprenticeships · pathways" },
  { icon: <Briefcase className="w-5 h-5" />, label: "Professionals", note: "Verified opportunities · proof of work" },
  { icon: <Building2 className="w-5 h-5" />, label: "Entrepreneurs & MSMEs", note: "Factories · service teams · supply chains" },
  { icon: <Users className="w-5 h-5" />, label: "Community Members", note: "Trusted services · safe collaboration" },
  { icon: <School className="w-5 h-5" />, label: "Institutions & NGOs", note: "Civic bodies · training centers" },
  { icon: <Wrench className="w-5 h-5" />, label: "Service Providers", note: "Riders · agents · technicians · designers" },
  { icon: <Tractor className="w-5 h-5" />, label: "Local Operators", node: "Workshops · studios · hubs", note: "Workshops · studios · local hubs" },
  { icon: <HeartHandshake className="w-5 h-5" />, label: "Families", note: "Structured economic participation" },
];

const WhoItIsFor = () => {
  return (
    <section
      id="who-its-for"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      data-testid="who-its-for-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <div className="eyebrow mb-4">04 · Who it is for</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
                A common ground for
                <br /> <span className="italic text-[#58D2C7]">everyone who builds</span>.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5">
            <Reveal delay={120}>
              <p className="text-[#6F7787] text-base md:text-lg leading-relaxed">
                From the youth seeking their first apprenticeship to the
                institution coordinating civic action — Believers Common is
                designed for every honest contributor.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {AUDIENCE.map((a, i) => (
            <Reveal key={a.label} delay={i * 60}>
              <div className="bg-[#0A0D14] p-8 h-full flex flex-col justify-between hover:bg-[#0F1320] transition-colors">
                <div className="text-[#D6B25E]">{a.icon}</div>
                <div className="mt-12">
                  <h3 className="font-serif-display text-2xl mb-2">{a.label}</h3>
                  <p className="text-[#6F7787] text-sm leading-relaxed">
                    {a.note}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- HOW IT WORKS ------------------------------- */

const STEPS = [
  { n: "I", title: "Register", text: "Create your member profile in the trusted commons." },
  { n: "II", title: "Verify", text: "Identity, role and references are verified for accountability." },
  { n: "III", title: "Participate", text: "Join the network as a member, professional, business or institution." },
  { n: "IV", title: "Serve", text: "Offer or access services with milestone-based, dispute-safe rules." },
  { n: "V", title: "Record Proof", text: "Work, claims and evidence are recorded transparently." },
  { n: "VI", title: "Build Reputation", text: "Earn standing through honest contribution, not privilege." },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      data-testid="how-it-works-section"
    >
      <div className="absolute inset-0 bc-grid bc-grid-fade opacity-30" />
      <div className="relative max-w-[1400px] mx-auto">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <div className="eyebrow mb-4">05 · How it works</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Six steps from
                <br /> intent to <span className="italic text-[#D6B25E]">reputation</span>.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5">
            <Reveal delay={120}>
              <p className="text-[#6F7787] text-base md:text-lg leading-relaxed">
                A simple, transparent path through the commons — designed so
                that participation is fair, traceable and protected from
                exploitation.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-x-12">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-white/10" />
          {STEPS.map((s, i) => (
            <Reveal
              key={s.n}
              delay={i * 80}
              className={`relative py-10 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:col-start-2"}`}
            >
              <div className="hidden md:block absolute top-12 w-3 h-3 rounded-full bg-[#D6B25E]" style={{ [i % 2 === 0 ? "right" : "left"]: "-7px" }} />
              <div
                className={`flex items-baseline gap-4 ${i % 2 === 0 ? "md:justify-end" : ""}`}
              >
                <span className="font-serif-display italic text-[#D6B25E] text-3xl">
                  {s.n}.
                </span>
                <h3 className="font-serif-display text-3xl md:text-4xl">
                  {s.title}
                </h3>
              </div>
              <p className="mt-3 text-[#6F7787] leading-relaxed max-w-md md:ml-auto">
                {s.text}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- TRUST & GOVERNANCE ------------------------------- */

const TrustGovernance = () => {
  const points = [
    { icon: <ShieldCheck className="w-4 h-4" strokeWidth={1.6} />, t: "Verified Identity", d: "Every member, role and contribution is identified and accountable." },
    { icon: <FileCheck2 className="w-4 h-4" strokeWidth={1.6} />, t: "Evidence Records", d: "Work, claims and decisions are supported by proof — not promises." },
    { icon: <Scale className="w-4 h-4" strokeWidth={1.6} />, t: "Fair Operating Rules", d: "Pricing bands, escalation paths and mediation protect participants." },
    { icon: <CircleDot className="w-4 h-4" strokeWidth={1.6} />, t: "No Hidden Privilege", d: "No nepotism. No backchannels. Participation is fair and transparent." },
    { icon: <Compass className="w-4 h-4" strokeWidth={1.6} />, t: "Dispute Resolution", d: "Structured mediation and clear pathways before any escalation." },
    { icon: <Sparkles className="w-4 h-4" strokeWidth={1.6} />, t: "Reputation Over Time", d: "Long-term standing built through honest, repeated contribution." },
  ];
  return (
    <section
      id="governance"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(31,122,90,0.18), transparent 60%), #0A0D14",
      }}
      data-testid="trust-governance-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <Reveal>
            <div className="eyebrow mb-4" style={{ color: "#58D2C7" }}>
              06 · Trust & Governance
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
              Built on{" "}
              <span className="italic text-[#1F7A5A]">ethical</span>
              <br />
              <span className="text-[#F6F7FB]">infrastructure</span>.
            </h2>
            <p className="mt-8 text-[#6F7787] leading-relaxed max-w-md">
              Believers Common is governed by founding principles — designed to
              protect dignity, fairness, and long-term collective prosperity.
            </p>
            <Reveal delay={150}>
              <Link to="/about" className="btn-ghost mt-10" data-testid="governance-learn-more">
                Read founding principles
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
              </Link>
            </Reveal>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
          {points.map((p, i) => (
            <Reveal key={p.t} delay={i * 60}>
              <div className="bg-[#0A0D14] p-7 h-full">
                <div className="flex items-center gap-2 text-[#1F7A5A] mb-4">
                  <span className="w-7 h-7 border border-[#1F7A5A]/40 flex items-center justify-center">
                    {p.icon}
                  </span>
                  <span className="text-[10px] tracking-[0.32em] uppercase">
                    Principle
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl mb-2">{p.t}</h3>
                <p className="text-[#6F7787] text-sm leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- PHYSICAL + DIGITAL ------------------------------- */

const PhysicalDigital = () => {
  return (
    <section
      id="commons"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      data-testid="commons-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-10 items-stretch">
        <div className="col-span-12 md:col-span-6">
          <Reveal>
            <div className="eyebrow mb-4">07 · The Commons</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
              Physical hubs.
              <br />
              <span className="italic text-[#D6B25E]">Digital trust.</span>
              <br />
              One commons.
            </h2>
            <p className="mt-8 text-[#6F7787] leading-relaxed max-w-lg">
              Believers Common connects workshops, studios, identity hubs, local
              factories, and community meeting points with a digital layer for
              records, services, payments and accountability.
            </p>
            <ul className="mt-10 space-y-5">
              {[
                ["Workshop & Studio", "Hands-on apprenticeship and making."],
                ["Identity Hub", "Verified onboarding and member records."],
                ["Service Centre", "Trusted business-call & local service rails."],
                ["Community Square", "Civic discussion, mediation, learning."],
              ].map(([t, d]) => (
                <Reveal key={t} className="flex items-start gap-4">
                  <span className="mt-2 w-5 h-px bg-[#D6B25E]" />
                  <div>
                    <div className="font-serif-display text-2xl">{t}</div>
                    <div className="text-[#6F7787] text-sm">{d}</div>
                  </div>
                </Reveal>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="col-span-12 md:col-span-6">
          <Reveal delay={120} className="relative h-full min-h-[560px]">
            <div className="absolute inset-0 border border-white/10" />
            <div
              className="absolute inset-3 bg-cover bg-center grayscale-[20%]"
              style={{
                backgroundImage:
                  "url('https://images.pexels.com/photos/18160274/pexels-photo-18160274.jpeg')",
              }}
            />
            <div
              className="absolute inset-3"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,13,20,0.1) 0%, rgba(10,13,20,0.85) 100%)",
              }}
            />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="eyebrow mb-2" style={{ color: "#58D2C7" }}>
                  Hub · Karnataka
                </div>
                <div className="font-serif-display text-3xl md:text-4xl leading-tight max-w-md">
                  A physical commons being prepared as the first node.
                </div>
              </div>
              <div className="hidden md:flex flex-col items-end text-right">
                <div className="bc-num text-[10px] tracking-[0.32em] uppercase text-[#6F7787]">
                  Lat 12.97
                </div>
                <div className="bc-num text-[10px] tracking-[0.32em] uppercase text-[#6F7787]">
                  Lng 77.59
                </div>
              </div>
            </div>
            <div className="absolute top-6 left-6 inline-flex items-center gap-2 backdrop-blur-md bg-[#0A0D14]/60 border border-white/10 px-3 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#58D2C7] bc-pulse" />
              <span className="text-[10px] tracking-[0.32em] uppercase">
                Under development
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------- FOUNDER'S NOTE ------------------------------- */

const FoundersNote = () => {
  return (
    <section
      className="relative bc-light py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-y border-black/10"
      data-testid="founders-note-section"
    >
      <div className="max-w-3xl mx-auto text-center">
        <Reveal>
          <div className="eyebrow mb-6">08 · Founder’s Note</div>
        </Reveal>
        <Reveal delay={120}>
          <p className="font-serif-display text-3xl md:text-5xl leading-[1.18] italic text-[#0A0D14]">
            “Believers Common was initiated as a mission-led platform to rebuild
            trust, organize contribution, and create a safer structure for
            community, work, service, and enterprise participation.”
          </p>
        </Reveal>
        <Reveal delay={240}>
          <p className="mt-10 text-[#6F7787] max-w-2xl mx-auto leading-relaxed">
            The founder’s role is not to become the face of the community, but
            to establish the mandate, protect the founding principles, and help
            create the first working systems through which members can
            participate with dignity, accountability, and purpose.
          </p>
        </Reveal>
        <Reveal delay={360}>
          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="w-10 h-px bg-[#0A0D14]/30" />
            <div className="font-serif-display italic text-[#1F7A5A] text-xl">
              The Founder
            </div>
            <div className="w-10 h-px bg-[#0A0D14]/30" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------- JOIN ------------------------------- */

const JoinSection = () => {
  return (
    <section
      id="join"
      className="relative py-20 md:py-32 px-5 sm:px-6 lg:px-10 border-t border-white/5"
      data-testid="join-section"
    >
      <div className="absolute inset-0 bc-grid bc-grid-fade opacity-40" />
      <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <Reveal>
            <div className="eyebrow mb-4">09 · Join the founding cohort</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.04]">
              Register your <span className="italic text-[#D6B25E]">interest</span>.
              <br />
              Become a founding member.
            </h2>
            <p className="mt-8 text-[#6F7787] leading-relaxed max-w-md">
              The first layer of trust often happens in conversation. Share a
              few details and we&apos;ll continue on WhatsApp — directly, without
              spam, without noise.
            </p>
            <div className="mt-10 space-y-4 text-sm text-[#6F7787]">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D6B25E]" /> Verified
                onboarding · founding cohort
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#58D2C7]" /> Direct
                line via WhatsApp · no automated funnels
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1F7A5A]" /> Your
                information is used only for onboarding & coordination
              </div>
            </div>
          </Reveal>
        </div>

        <div className="col-span-12 md:col-span-7">
          <Reveal delay={120}>
            <div className="border border-white/10 p-6 sm:p-8 md:p-12 bg-[#0F1320]/40 backdrop-blur-sm">
              <WhatsAppForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Home;
